import {
  useDebouncedCallback,
  useDebouncedValue,
  useFocusTrap,
  useMergedRef,
} from '@mantine/hooks';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  LinearProgressProps,
  Modal,
  ModalProps,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  HandleClasses,
  Rnd,
  RndDragCallback,
  RndResizeCallback,
  RndResizeStartCallback,
} from 'react-rnd';
import { useTranslation } from 'react-i18next';
import { bloxValues } from 'themes/constants';
import { getSolidThemeGray } from 'utility/styles';

/**
 * Drag Resize Modal Component - info / work in progress
 *
 * https://mui.com/material-ui/react-modal/
 * https://github.com/bokuweb/react-rnd
 * https://github.com/bokuweb/react-rnd/issues/700
 * https://github.com/react-grid-layout/react-draggable/issues/622
 *
 * note: math rounding, drag grid values, resize grid values, and scale 1
 * are all attempting to deal with subpixel render blur issues
 * causing the modal to look blurry in certain cases
 * as rnd library uses transform css to position it around
 * sometimes can get a weird x/y like 123.55, 67.55
 * so we round the values to ensure we are not getting
 * subpixel values in the state and also trying to ensure
 * that its snapped to some sort of pixel grid as well...
 *
 */

/**
 * todo:
 * the mui modal by default can take a 'container' prop but we omitted it for now
 * as we need to improve the component regarding boundary/size/pos calcs first.
 * Saving helper fn to resolve the modal container element if needed in future.
 * If we do implement this, we will need to adjust all of the math and props
 * to work when passing a container prop/ref to DragModal as the boundaries.
 *
 * Note that due to the way we have workarounds for react-admins layout the
 * document.body (mui default) will always have DOMrect zeros since we set display:contents
 * on the <html> and <body> tags so when passing a ref/el via container
 * we need to resolve it by manually find it, or if omitted we likely
 * should resolve the default element to something like id='main-layout-grid'
 * or root or some other container element that does not have display:contents set
 *
 * At the time of this comment both of those elements should have readable
 * DOMRect values so we can create a bounding rect for the modal.
 * see commit 00b72d256bfdb8ac6932a3ec822fe261c16cf5cf for ideation.
 * We dont currently have a solid use case for restricting modal to
 * a container element, but we may need to do this in the future.
 *
 * During testing on that commit as well, ran into some issues with rnd going blank
 * when resized/dragged but likely was due to incorrect calculations or an issue with the library.
 * Same issue applied to the drag handles, they were not sized properly, and because of the
 * nature of refs needing to be attached first before able to access their current values we
 * would need to add some sort of layoutEffect to the DragModal to reinitialize the state
 * once the passed element/ref is ready to be used and current, so the initial position/size
 * calculations are properly respected (open to center or offset, size, init W/H, etc)
 * and perhaps the parent element should block mount/render of the modal if using container prop
 * until it confirms attachment of the ref to whichever element is passed in as the container.
 *
 * Also note it seemed problematic trying to restrict the modal to a container with the
 * given the defaults, like the vw/vh minmax those may need to be changed to percentages if a custom
 * container is used and just allow 100% instead when a container el is specified, as well as some
 * MUI issues, eg the default modal backdrop seems to just fill the entire screen anyway and
 * not just be overlayed on the container specified, also unsure of how this would affect the
 * zindex or stacking context of the modals/backdrops, as the mui handles some of that internally.
 * We may be better off reworking some of that or overriding those props manually on MUI
 * when a container prop is passed in, or just using a custom solution rather than the mui modal.
 */

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function resolveModalContainer(containerProp?: ModalProps['container']): Element {
  if (containerProp) {
    if (typeof containerProp === 'function') {
      const resolved = containerProp();
      if (resolved instanceof Element) {
        return resolved;
      }
    } else if (containerProp instanceof Element) {
      return containerProp;
    }
  }
  // Try '#main-layout-grid' if available
  const mainLayoutEl = document.getElementById('main-layout-grid');
  if (mainLayoutEl) {
    return mainLayoutEl;
  }
  // Try '#root' if available
  const rootEl = document.getElementById('root');
  if (rootEl) {
    return rootEl;
  }
  // fallback to document.body as mui default but please do note that
  // in cosmos this wont have a true DOMRect since we use display:contents
  // on it because we had to work around some react-admin layout problems
  // which may cause initial size/pos/constraints to be incorrect (all 0s)
  return document.body;
}

/**
 * Sanitize React's useId() into a CSS safe string.
 * Supposedly this is fixed in React 19
 * https://github.com/facebook/react/issues/26839
 * https://github.com/facebook/react/pull/32001
 *
 * React useId() often returns strings containing colons, e.g. `":r1m:"`,
 * which is not safe for CSS eg, doc.querySelector() or element.matches().
 *
 *  // element.matches('.drag-modal-:r1m:-header')
 *  // results: Failed to execute 'matches' on 'Element':
 *  // '.drag-modal-:r1m:-header' is not a valid selector.
 *
 * Helper replaces colons with __, and other invalid chars with an _.
 * and then trims any leading/trailing hyphens.
 */
export function sanitizeReactUseId(rawId: string) {
  if (!rawId || typeof rawId !== 'string') {
    console.warn('[fixReactId] Invalid paramater:', rawId);
    return;
  }

  // trim pre and post replacements
  // replace colons with an underscore
  // anything else non-alphanumeric becomes a dash
  const sanitized = rawId
    .trim()
    .replace(/:/g, '_')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .trim();

  return sanitized;
}

//--------------------------------------------------------------------
// Types, Consts, Helper Functions
//--------------------------------------------------------------------

// MuiCloseEvent library type looks wrong - its just an empty obj?
// type MuiCloseEvent = Parameters<NonNullable<ModalProps['onClose']>>[0];
export type MuiCloseReasons = Parameters<NonNullable<ModalProps['onClose']>>[1];
export type PossibleCloseEvent =
  | React.SyntheticEvent
  | React.BaseSyntheticEvent
  | React.SyntheticEvent<Element, Event>
  | React.MouseEvent<HTMLButtonElement, MouseEvent>
  | React.MouseEvent<HTMLDivElement, MouseEvent>;

// Extend close reasons for better dx
export type CustomCloseReasons =
  | MuiCloseReasons
  | 'closeButton'
  | 'confirmClose'
  | 'cancelClose'; // cancel confirm close (keep open)

// Omit the props we dont want to allow
export type SelectedModalProps = Omit<
  ModalProps,
  'open' | 'onClose' | 'children' | 'container'
>;

// If passing confirm close jsx enforce props on it
// component should accept minimally open, onConfirm, onCancel)
export type CustomConfirmCloseComponent = React.ComponentType<BaseConfirmCloseProps>;

// number for raw px, css string (eg '80vh') will attempt conversion to px
export type SizeValue = string | number;

export type SizeAndPos = {
  width: number;
  height: number;
  x: number;
  y: number;
};

// minmax state for modal bounds and calcs
// in px, eg 300, 200, and percent in
// decimal form, eg 0.5 for 50%
type MinMaxState = {
  minWidth: number; // ints (as px)
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  initWidth: number;
  initHeight: number;
  maxWidthPercent: number; // float 0.5 (for 50%)
  maxHeightPercent: number; // float same as above
};

export type InitialPosition = {
  x?: number | string; // number: raw desired pixel offset
  y?: number | string; // string: '50%' (with percent) for offset of the parent container
};

/**
 * Control init props config - opt in only.
 * This option makes initial H/W and pos props reactive.
 * This is useful when the client can statically compute
 * up front the sizes and change the initial H/W props
 * via states, for example a stepper/wizard modal component
 * where we know what the estimated height is on each step.
 * - const modalH = onStep1 ? 400 : onStep2 ? 600 : 500;
 * - <DragModal initH={modalH} controlInitProps={{ enabled: true ... }} />
 *
 * Generally this works best with a centered fixed modal
 * however it can be also be used to setup 'autosizing' by
 * controlling the enabled state and using measuring refs
 * with companion "useAutoSizeModal" hook... (in dev)
 *
 */

export type ControlInitPropsConfig = {
  enabled?: boolean;
  updatePos?: boolean;
};

const CTRL_INIT_PROPS_CFG: ControlInitPropsConfig = {
  enabled: false,
  updatePos: false,
};

export type ResizeHandlesConfig = {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
  topRight: boolean;
  bottomRight: boolean;
  bottomLeft: boolean;
  topLeft: boolean;
};

// default all resize handles config
export const ALL_HANDLES: ResizeHandlesConfig = {
  top: true,
  right: true,
  bottom: true,
  left: true,
  topRight: true,
  bottomRight: true,
  bottomLeft: true,
  topLeft: true,
};

// hot corners only config for resize handles (eg macos style)
export const CORNERS_ONLY: ResizeHandlesConfig = {
  top: false,
  right: false,
  bottom: false,
  left: false,
  topRight: true,
  bottomRight: true,
  bottomLeft: true,
  topLeft: true,
};

/**
 * Ref props type - either:
 * - callback ref (eg createRef / mantine useResizeObserver)
 * - object ref (eg via normal useRef)
 */
type RefPropsType = React.Ref<HTMLDivElement | null>;

/**
 * Note: if not resizeable, the initial w/h is also
 * used as a 'target' or 'desired' size so upon browser resizes, it
 * will attempt to grow the modal back to that initial size
 * while still respecting the min/max sizes and bounds.
 * eg if the modal was shrunk due to the browser
 * size changing, and then the browser is expanded again,
 * it should attempt to grow the modal back to the init target w/h,
 * so the modal wont be 'stuck' at a smaller size.
 */
export interface DragModalProps extends SelectedModalProps {
  open: ModalProps['open'];
  onClose?: (event: PossibleCloseEvent, reason?: CustomCloseReasons) => void;
  isDraggable?: boolean;
  isResizable?: boolean;
  resizeHandles?: ResizeHandlesConfig; // resize handles config
  isFullscreenable?: boolean;
  header?: React.ReactNode;
  body?: React.ReactNode;
  footer?: React.ReactNode;
  // paper sx, overall modal sx is still 'sx' prop (inherited)
  paperSx?: SxProps<Theme>;
  bodySx?: SxProps<Theme>;
  preventBackdropClickClose?: boolean;
  preventEscapeKeyClose?: boolean;
  shouldConfirmClose?: boolean; // should show confirm close overlay?
  initialPosition?: InitialPosition; // initial position of the modal
  initialWidth?: SizeValue; // target start width of the modal (px, vw, vh, %)
  initialHeight?: SizeValue; // target start height of the modal (px, vw, vh, %)
  minWidth?: SizeValue; // min width
  minHeight?: SizeValue; // min height
  maxWidth?: SizeValue; // max width
  maxHeight?: SizeValue; // max height
  // Optional controlled props - see type comment for usage
  controlInitProps?: ControlInitPropsConfig;
  // Refs attached to containers (eg: for measurements)
  headerRef?: RefPropsType;
  bodyRef?: RefPropsType;
  footerRef?: RefPropsType;
  // Optionally pass a custom confirm close overlay component - just ensure it can accept the base props
  ConfirmCloseJSX?: CustomConfirmCloseComponent;
  // If using default (no override) confirm close can override default props here
  defaultConfirmCloseProps?: DefaultConfirmCloseProps;
  // show linear progress bar at bottom of header area
  showLoadingBar?: boolean;
  // linear progress props if override needed
  linearProgressProps?: LinearProgressProps;
  // mask and/or inert the modal when async operations are in progress
  shouldMask?: boolean;
  shouldInert?: boolean;
  hideBackdrop?: boolean;
  // Allow drag and drop from modal content to elements outside/behind the modal
  allowDragThrough?: boolean;
}

// Default constants
const DEFAULT_ID = 'drag-modal';
const PAD_VAL = 1; // sx padding value
const MODAL_MIN_W = 300; // modal minmax
const MODAL_MIN_H = 200;
export const MODAL_DEF_VW = '85vw';
export const MODAL_DEF_VH = '85vh';
// max larger than default so draghandles can be grabbed
const MODAL_MAX_VW = '100vw';
const MODAL_MAX_VH = '100vh';
const INIT_POS_X = '50%'; // default centered x pos of container/doc.body
const INIT_POS_Y = '50%'; // default centered y pos of container/doc.body
const INITIAL_POSITION = { x: INIT_POS_X, y: INIT_POS_Y }; // default initial position 50% 50%

/**
 * Utility sx for bodySx prop, overrides padding
 * and scrollbarGutter to allow filling entire
 * body area with full bleed edge-to-edge content.
 * This is used for some more complex modals which
 * need to entirely fill the body area with no 'padding'
 * including "scrollbar gutter" space which looks like padding.
 * note: unstable/auto scrollbarGutter can make
 * the inner contents shift during user drag events.
 */
export const FULL_BODY_SX: SxProps<Theme> = {
  padding: 0,
  scrollbarGutter: 'auto',
};

// Default linear progress props (network call loading)
export const linearLoadingProps: LinearProgressProps = {
  variant: 'indeterminate',
  color: 'primary',
  value: undefined,
};

// Default error linear progress (network call failed)
export const linearErrorProps: LinearProgressProps = {
  variant: 'determinate',
  color: 'error',
  value: 100,
};

type AxisTarget = 'width' | 'height';

type ConvertCssSizeToPxParams = {
  size: SizeValue;
  axis?: AxisTarget;
  round?: boolean;
  containerEl?: Element;
};

/**
 * Helper to convert/estimate CSS size units to pixels for initial size calc
 * note: calling this often can be expensive since for string values
 * it creates and removes an element to extract the computed size
 * so if you need to call this often, may want to memoize results or debounce calls
 * by default will Math.round() the result to avoid subpixel issues
 */
function convertCssSizeToPx({
  size,
  axis = 'width',
  round = true,
  containerEl,
}: ConvertCssSizeToPxParams): number {
  // If already a number, return as likely in px already
  if (typeof size === 'number') {
    return size;
  }
  // Create temp el and apply styles for measurement
  const element = document.createElement('div');
  element.style.position = 'absolute';
  element.style.visibility = 'hidden';
  element.style.height = '0';
  element.style.padding = '0';
  element.style.margin = '0';
  element.style.border = 'none';
  element.style.boxSizing = 'content-box';
  element.style.whiteSpace = 'nowrap';
  // Set the size to the desired axis
  if (axis === 'width') {
    element.style.width = size;
    element.style.height = '0';
  } else {
    element.style.height = size;
    element.style.width = '0';
  }
  // Append to specified parent or body
  const target = containerEl || document.body;
  target.appendChild(element);
  // get computed style and extract the pixel value
  const computed = getComputedStyle(element);
  const pixels =
    axis === 'width' ? parseFloat(computed.width) : parseFloat(computed.height);
  // Remove the temporary element
  target.removeChild(element);
  // Handle NaN or invalid values (can happen in test environments like JSDOM)
  if (!isFinite(pixels) || isNaN(pixels)) {
    return 0;
  }
  // Return rounded or raw result (always min 0)
  const result = round ? Math.round(pixels) : pixels;
  const clamped = Math.max(0, result);
  return clamped;
}

type ResolvePositionValueParams = {
  val: InitialPosition['x'] | InitialPosition['y'];
  axis: 'x' | 'y';
  size: number;
  containerEl?: ConvertCssSizeToPxParams['containerEl'];
};

function resolvePositionValue({
  val,
  axis,
  size,
  containerEl,
}: ResolvePositionValueParams) {
  // passed in pixel value
  if (typeof val === 'number') {
    return Math.max(0, val);
  }
  // passed in css percent string
  if (typeof val === 'string' && val.endsWith('%')) {
    const containerSize = convertCssSizeToPx({
      size: '100%',
      axis: axis === 'x' ? 'width' : 'height',
      round: true,
      containerEl: containerEl,
    });
    // convert percentage string to a decimal
    const percent = Math.max(0, parseFloat(val) / 100);
    // passed size should not exceed container (eg window) max size
    const effectiveSize = Math.min(size, containerSize);
    // calc available space in the container, at least 0
    const availableSpace = Math.max(0, containerSize - effectiveSize);
    // calculate offset based on available container space
    const offset = Math.round(percent * availableSpace);
    // ensure result is at least zero
    const clamped = Math.max(0, offset);
    return clamped;
  }
  // fallback in case this doesnt resolve correctly
  // will basically return (0,0) if fails for top left corner
  // but alt can do calc on 50/50 of viewport / container size?
  return 0;
}

/**
 * Misc internal types / helper fns
 */

type CalcMinMaxResult = {
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  initWidth: number;
  initHeight: number;
  maxWidthPercent: number;
  maxHeightPercent: number;
};

type CalcSizeAndPosArgs = {
  initWidth: number;
  initHeight: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  maxWidthPercent: number;
  maxHeightPercent: number;
};

/**
 * DragModal Component
 */
export const DragModal = memo(
  forwardRef<HTMLDivElement | null, DragModalProps>(
    (
      {
        header,
        body,
        footer,
        open,
        onClose,
        paperSx,
        bodySx,
        id = DEFAULT_ID,
        initialPosition = INITIAL_POSITION,
        initialWidth = MODAL_DEF_VW,
        initialHeight = MODAL_DEF_VH,
        minWidth = MODAL_MIN_W,
        minHeight = MODAL_MIN_H,
        maxWidth = MODAL_MAX_VW,
        maxHeight = MODAL_MAX_VH,
        controlInitProps = CTRL_INIT_PROPS_CFG,
        headerRef,
        bodyRef,
        footerRef,
        isDraggable = false,
        isResizable = false,
        resizeHandles = ALL_HANDLES,
        isFullscreenable = false,
        shouldConfirmClose = false,
        preventBackdropClickClose = false,
        preventEscapeKeyClose = false,
        showLoadingBar = false,
        shouldMask = false,
        shouldInert = false,
        ConfirmCloseJSX = DefaultConfirmCloseOverlay,
        defaultConfirmCloseProps = defaultCloseProps,
        linearProgressProps = linearLoadingProps,
        hideBackdrop = false,
        allowDragThrough = false,
        ...props
      }: DragModalProps,
      ref,
    ) => {
      // needs stable unique id for multiple stacked dragmodals
      const reactId = useId();
      const theme = useTheme();
      const { t } = useTranslation();
      const transitionDurationMs = theme.transitions.duration.standard;

      // Translation strings
      const strClose = t('blox.actions.close');
      const strRestore = t('blox.actions.restore');
      const strMaximize = t('blox.actions.maximize');

      // tracking viewport size for scaling position logic
      // note: on very first render this can be 0s
      const windowSizeRef = useRef({
        width: Math.max(0, Math.round(document.documentElement.clientWidth)),
        height: Math.max(0, Math.round(document.documentElement.clientHeight)),
      });

      // update window size ref and return the ref data
      const setWindowSizeRef = useCallback(() => {
        const newW = Math.max(0, Math.round(document.documentElement.clientWidth));
        const newH = Math.max(0, Math.round(document.documentElement.clientHeight));
        windowSizeRef.current = { width: newW, height: newH };
        return windowSizeRef.current;
      }, []);

      // Convert size values to pixels for easier calculations
      // future note: if we decide to do container restrictions, will need to forward
      // the passed in el to the convert fns, windowResizeHandler, and bounds checker
      // to account for the container el size/position in the calculations
      // also see comments on resolveModalContainer for issues related
      const calcMinMax = useCallback(() => {
        const minWidthPx = convertCssSizeToPx({ size: minWidth, axis: 'width' });
        const minHeightPx = convertCssSizeToPx({ size: minHeight, axis: 'height' });
        const maxWidthPx = convertCssSizeToPx({ size: maxWidth, axis: 'width' });
        const maxHeightPx = convertCssSizeToPx({ size: maxHeight, axis: 'height' });
        const initWidthPx = convertCssSizeToPx({ size: initialWidth, axis: 'width' });
        const initHeightPx = convertCssSizeToPx({ size: initialHeight, axis: 'height' });

        // Convert for example a vh/vw string to a percentage of the screen size
        // makes it easier to calculate the max size of the modal in certain
        // areas to just multiply the window size by a decimal percentage...
        // eg: calcMaxWidthPct if passed in '85%' should become 0.85 here...
        // Guard against division by zero in test environments (JSDOM)
        const clientWidth = document.documentElement.clientWidth || 1;
        const clientHeight = document.documentElement.clientHeight || 1;
        const desiredMaxPctOfScreenWidth = Number((maxWidthPx / clientWidth).toFixed(2));
        const clampedWidthPct = Math.max(0, desiredMaxPctOfScreenWidth);
        // clamp to 2 decimal places for simplicity
        const desiredMaxPctOfScreenHeight = Number(
          (maxHeightPx / clientHeight).toFixed(2),
        );
        // safety: ensure min value is at least 0
        const clampedHeightPct = Math.max(0, desiredMaxPctOfScreenHeight);

        // convertCssSizeToPx already rounds to ints for px
        // do not round percents, keep as float decimal
        const result: CalcMinMaxResult = {
          minWidth: minWidthPx,
          minHeight: minHeightPx,
          maxWidth: maxWidthPx,
          maxHeight: maxHeightPx,
          initWidth: initWidthPx,
          initHeight: initHeightPx,
          maxWidthPercent: clampedWidthPct,
          maxHeightPercent: clampedHeightPct,
        };

        return result;
      }, [minWidth, minHeight, maxWidth, maxHeight, initialWidth, initialHeight]);

      // minmax and percentage state for modal/window
      const [minMax, setMinMax] = useState<MinMaxState>(() => calcMinMax());

      // parts of the minMax state for easier access
      const stateMinW = minMax.minWidth;
      const stateMinH = minMax.minHeight;
      const stateMaxW = minMax.maxWidth;
      const stateMaxH = minMax.maxHeight;
      const stateInitW = minMax.initWidth;
      const stateInitH = minMax.initHeight;
      const stateMaxWPct = minMax.maxWidthPercent;
      const stateMaxHPct = minMax.maxHeightPercent;

      // other attributes and states for component
      const inertAttrs = useMemo(() => (shouldInert ? { inert: '' } : {}), [shouldInert]);
      const hasFooter = Boolean(footer);
      const rndRef = useRef<Rnd>(null);
      const focusTrapRef = useRef<HTMLDivElement | null>(null);
      const [showConfirmClose, setShowConfirmClose] = useState(false); // confirm close overlay handling
      const [isDragging, setIsDragging] = useState(false); // dragging state for scroll locking etc
      const [isResizing, setIsResizing] = useState(false); // resizing state for scroll locking etc
      const [isFullscreen, setIsFullscreen] = useState(false); // fullscreen mode btn toggle
      const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
      const isMobileOrFull = isMobile || isFullscreen;
      const midDragOrResize = isDragging || isResizing;
      const showFullscreenButton = isFullscreenable && !isMobile;
      const isDragAllowed = isDraggable && !isMobile && !isMobileOrFull;

      // Conditions to allow modal resize functionality
      const isResizeAllowed =
        isResizable && !isMobile && !isDragging && !showConfirmClose && !isMobileOrFull;

      // props no drag, no resize, fixed modal like standard MUI style
      const isStaticModal = isResizable === false && isDraggable === false;

      // custom onClose handling with confirm dialog optional
      const internalOnClose = useCallback(
        (event: PossibleCloseEvent, reason?: CustomCloseReasons) => {
          // if confirm close overlay is active
          // do nothing (prevent modal from closing)
          // the action must come from the confirm dialog
          if (showConfirmClose) {
            return;
          }
          // Check for prevented  close conditions
          if (reason === 'backdropClick' && preventBackdropClickClose) {
            return;
          }
          if (reason === 'escapeKeyDown' && preventEscapeKeyClose) {
            return;
          }
          // If we need to confirm close show the confirm overlay
          if (shouldConfirmClose && !showConfirmClose) {
            setShowConfirmClose(true);
            return;
          }
          // passed checks call parent onClose handler
          if (onClose) {
            onClose(event, reason);
          }
        },
        [
          onClose,
          preventBackdropClickClose,
          preventEscapeKeyClose,
          shouldConfirmClose,
          showConfirmClose,
        ],
      );

      // Handle confirm or cancel close dialog
      // when confirming, reason should be 'confirmClose'
      // when cancelling, reason should be 'cancelClose'
      const handleConfirmCancelClose = useCallback(
        (event: PossibleCloseEvent, reason?: CustomCloseReasons) => {
          setShowConfirmClose(false);
          // forward to parent onClose
          if (onClose) {
            onClose(event, reason);
          }
        },
        [onClose],
      );

      /**
       * Calculate modal size and position via args.
       * Result is size and position that should be set.
       * Note: maxPercent args are unused but passed
       * in just to keep the fn arguments consistent.
       */
      const calcSizeAndPos = useCallback(
        ({
          initWidth,
          initHeight,
          minWidth,
          minHeight,
          maxWidth,
          maxHeight,
          maxWidthPercent,
          maxHeightPercent,
        }: CalcSizeAndPosArgs): SizeAndPos => {
          // Mobile and fullscreen considerations
          const defaultWidth = isMobileOrFull
            ? document.documentElement.clientWidth
            : initWidth;

          const defaultHeight = isMobileOrFull
            ? document.documentElement.clientHeight
            : initHeight;

          // Clamp so x/y resolved are derived via
          // the actual size that will be applied
          const width = Math.round(Math.max(minWidth, Math.min(defaultWidth, maxWidth)));
          const height = Math.round(
            Math.max(minHeight, Math.min(defaultHeight, maxHeight)),
          );
          const x = Math.round(
            resolvePositionValue({ val: initialPosition?.x, axis: 'x', size: width }),
          );
          const y = Math.round(
            resolvePositionValue({ val: initialPosition?.y, axis: 'y', size: height }),
          );

          const result: SizeAndPos = { width, height, x, y };

          return result;
        },
        [isMobileOrFull, initialPosition],
      );

      // Control size and position of modal state...
      // Initialize state pattern using helper function
      // Initial arguments are via minMax state calculated from props
      const [sizeAndPosition, setSizeAndPos] = useState<SizeAndPos>(() =>
        calcSizeAndPos({
          initWidth: stateInitW,
          initHeight: stateInitH,
          minWidth: stateMinW,
          minHeight: stateMinH,
          maxWidth: stateMaxW,
          maxHeight: stateMaxH,
          maxHeightPercent: stateMaxHPct,
          maxWidthPercent: stateMaxWPct,
        }),
      );

      // parts of the size/pos state for easier access
      // round all values to avoid subpixel issues
      const modalW = Math.round(sizeAndPosition?.width);
      const modalH = Math.round(sizeAndPosition?.height);
      const modalX = Math.round(sizeAndPosition?.x);
      const modalY = Math.round(sizeAndPosition?.y);

      /**
       * Run effect when open state changes to
       * recalculate init size and position, so that
       * if a modal is mounted but not visible (eg keepMounted)
       * effect will recalc size/pos states upon open
       * this recalcs size/pos/minmax when reopened
       */
      const hasInitSizePosRef = useRef(false);
      useEffect(() => {
        const nextMinMax = calcMinMax();
        const nextSizePos = calcSizeAndPos(nextMinMax);
        if (open) {
          if (!hasInitSizePosRef.current) {
            hasInitSizePosRef.current = true;
            setWindowSizeRef();
            setMinMax(nextMinMax);
            setSizeAndPos(nextSizePos);
          }
        } else {
          hasInitSizePosRef.current = false;
          setWindowSizeRef();
          setMinMax(nextMinMax);
          setSizeAndPos(nextSizePos);
          setIsDragging(false);
          setIsResizing(false);
          setIsFullscreen(false);
          setShowConfirmClose(false);
        }
      }, [open, calcSizeAndPos, calcMinMax, setWindowSizeRef]);

      /**
       * controlInitProps effect - opt-in only
       * This option makes initial H/W and pos props reactive.
       * See prop type comments for usage and examples.
       * Generally works best with centered and fixed modals
       * where sizes can be estimated up front and props
       * controlled via a parent state, eg a fixed wizard/stepper:
       * <DragModal initW/H={step1 ? 300 : step2 ? 600 : 500} ... />
       */
      useEffect(() => {
        if (!open || !controlInitProps?.enabled) {
          return;
        }
        if (isMobileOrFull || midDragOrResize) {
          return;
        }
        // Recalc min/max once, then resolve size/pos from that same snapshot.
        const nextMinMax = calcMinMax();
        const newSizePos = calcSizeAndPos(nextMinMax);
        // Update minmax state with latest values
        setMinMax(nextMinMax);
        // Update size and position, x/y if updatePos requested, else keep prior.
        setSizeAndPos((prev) => {
          return {
            ...newSizePos,
            x: controlInitProps.updatePos ? newSizePos.x : prev.x,
            y: controlInitProps.updatePos ? newSizePos.y : prev.y,
          };
        });
      }, [
        open,
        initialWidth,
        initialHeight,
        initialPosition.x,
        initialPosition.y,
        controlInitProps.enabled,
        controlInitProps.updatePos,
        isMobileOrFull,
        midDragOrResize,
        calcSizeAndPos,
        calcMinMax,
      ]);

      /**
       * Window resize handler
       * With position scaling - will scale the position of the modal
       * based on the new window dimensions vs previous window dimensions
       * todo: maybe scale size of modal too -- attempted but the drag handles
       * positions were incorrect and showed some odd resize behavior post scaling
       *
       * Note: If not resizable, use the initial size as a target size
       * to try and ensure the modal 'grows' back to the desired size
       * example: the window was shrunk so the modal shrank to fit
       * then the user expands the window again, we dont want the modal to be stuck
       * at a smaller size if the window is now larger. one edge case
       * is due to above notes in resizable mode, but can allow the user
       * grab and resize it manually there. This should make non-user resizable
       * modals behave more like the default MUI Modals which also auto resize.
       *
       * Note: also recalculate the minMax states when window
       * is resized, so that the min/max vars are up to date
       * helping prevent a modal from being stuck in a smaller size
       * if the window started smaller and then was resized larger.
       *
       */

      const handleWindowResize = useCallback(
        (event: UIEvent) => {
          const prevWinSize = windowSizeRef.current;

          // update and get new window sizes via ref
          const { width: newWinWidth, height: newWinHeight } = setWindowSizeRef();

          // recalc min/max values based on new window size
          const newMinMax = calcMinMax();

          // use the newMinMax vals in here to keep synced
          const newInitW = newMinMax.initWidth;
          const newInitH = newMinMax.initHeight;
          const newMaxWPct = newMinMax.maxWidthPercent;
          const newMaxHPct = newMinMax.maxHeightPercent;

          // set newMinMax to state to keep synced
          setMinMax(newMinMax);

          // update modal size/pos using newMinMax calcs
          setSizeAndPos((prevPos) => {
            const modalMaxW = newWinWidth * newMaxWPct;
            const modalMaxH = newWinHeight * newMaxHPct;

            // -- setup target width and height calculations --
            // start off assuming standard non-resizable calculation
            // try using the initial size as the target sizes for the
            // modal while respecting min/max with clamping and rounding
            let targetWidth = Math.round(Math.min(newInitW, modalMaxW));
            let targetHeight = Math.round(Math.min(newInitH, modalMaxH));

            // edge case: if resizable, start from the previous modal size
            // instead of the init size, so that the modal will try to
            // remain the same size when the window is resized, but can
            // shrink down to a smaller size if the window becomes smaller
            if (isResizable === true) {
              targetWidth = Math.round(Math.min(prevPos.width, modalMaxW));
              targetHeight = Math.round(Math.min(prevPos.height, modalMaxH));
            }

            // ensure new w/h respects constraints and round for subpixel issues
            const newWidth = Math.round(Math.min(targetWidth, modalMaxW));
            const newHeight = Math.round(Math.min(targetHeight, modalMaxH));

            // -- setup offset position calculations --

            // relative percent offset from top-left (ensure no div by zero)
            const xPct = prevPos.x / Math.max(1, prevWinSize.width - prevPos.width);
            const yPct = prevPos.y / Math.max(1, prevWinSize.height - prevPos.height);

            // apply relative offset +/- to new window size
            const scaledX = Math.round(xPct * (newWinWidth - newWidth));
            const scaledY = Math.round(yPct * (newWinHeight - newHeight));

            // clamp offset to keep within visible bounds
            const clampedX = Math.max(0, Math.min(scaledX, newWinWidth - newWidth));
            const clampedY = Math.max(0, Math.min(scaledY, newWinHeight - newHeight));

            // assign new target X and Y (position)
            let targetX = clampedX;
            let targetY = clampedY;

            // edge case: static modal - not resizable, not draggable
            // likely we want to keep the modal centered or fixed
            // to a specific offset position on screen at all times
            // similar to the default mui centered dialog behavior.
            // in this case when window size changes recalculate the
            // position offset based on the initial position props
            // and use the new width/height to get the final position

            if (isStaticModal) {
              // recalc xy pos and round for subpixel issues
              const recalcPosX = Math.round(
                resolvePositionValue({
                  val: initialPosition?.x,
                  axis: 'x',
                  size: newWidth,
                }),
              );

              const recalcPosY = Math.round(
                resolvePositionValue({
                  val: initialPosition?.y,
                  axis: 'y',
                  size: newHeight,
                }),
              );

              // min zero to ensure modal in view
              targetX = Math.max(0, recalcPosX);
              targetY = Math.max(0, recalcPosY);
            }

            // return new size and position
            const newState = {
              width: newWidth,
              height: newHeight,
              x: targetX,
              y: targetY,
            };

            return newState;
          });
        },
        [
          calcMinMax,
          setWindowSizeRef,
          isResizable,
          isStaticModal,
          initialPosition?.x,
          initialPosition?.y,
        ],
      );

      // Small debounce to the window resize handler
      const dbHandleWindowResize = useDebouncedCallback(handleWindowResize, { delay: 2 });

      // Register window resize event listener
      useLayoutEffect(() => {
        window.addEventListener('resize', dbHandleWindowResize, { capture: true });
        return () =>
          window.removeEventListener('resize', dbHandleWindowResize, { capture: true });
      }, [handleWindowResize, dbHandleWindowResize]);

      // Handle rnd drag
      const handleDrag: RndDragCallback = useCallback(
        (e, d) => {
          setSizeAndPos((prev) => {
            const maxWidth = document.documentElement.clientWidth * stateMaxWPct;
            const maxHeight = document.documentElement.clientHeight * stateMaxHPct;
            const width = Math.round(Math.max(stateMinW, Math.min(prev.width, maxWidth)));
            const height = Math.round(
              Math.max(stateMinH, Math.min(prev.height, maxHeight)),
            );
            // Keep the modal within the viewport
            const x = Math.round(
              Math.max(0, Math.min(d.x, document.documentElement.clientWidth - width)),
            );
            const y = Math.round(
              Math.max(0, Math.min(d.y, document.documentElement.clientHeight - height)),
            );
            return { width, height, x, y };
          });
        },
        [stateMaxHPct, stateMaxWPct, stateMinH, stateMinW],
      );

      // Handle drag start event
      const handleDragStart: RndDragCallback = useCallback(
        (e, d) => {
          setIsDragging(true);
        },
        [setIsDragging],
      );

      // Handle drag stop and unlock certain css
      const handleDragStop: RndDragCallback = useCallback(
        (e, d) => {
          handleDrag(e, d); // forward on stop to ensure final position
          setIsDragging(false); // unlock certain css
        },
        [setIsDragging, handleDrag],
      );

      // Handle rnd resize
      // Ensure the new size respects the min/max constraints on paperSx
      // because a user can release the drag handle under/over the min/max and
      // the drag handles could get stuck in the min/max position that they were released at
      // so we need to enforce our state to track those min/max values so the drag handles
      // always stay in the positions we would expect them to be in
      const handleResize: RndResizeCallback = useCallback(
        (e, dir, ref, delta, pos) => {
          setSizeAndPos((prev) => {
            const maxWidth = document.documentElement.clientWidth * stateMaxWPct;
            const maxHeight = document.documentElement.clientHeight * stateMaxHPct;

            const width = Math.round(
              Math.max(stateMinW, Math.min(ref.offsetWidth, maxWidth)),
            );
            const height = Math.round(
              Math.max(stateMinH, Math.min(ref.offsetHeight, maxHeight)),
            );

            // Keep the modal within the viewport
            const x = Math.round(
              Math.max(0, Math.min(pos.x, document.documentElement.clientWidth - width)),
            );

            const y = Math.round(
              Math.max(
                0,
                Math.min(pos.y, document.documentElement.clientHeight - height),
              ),
            );

            return { width, height, x, y };
          });
        },
        [stateMaxHPct, stateMaxWPct, stateMinH, stateMinW],
      );

      // Handle resize start
      const handleResizeStart: RndResizeStartCallback = useCallback(
        (e, dir, elementRef) => {
          setIsResizing(true); // lock certain css
        },
        [setIsResizing],
      );

      // Handle resize stop
      const handleResizeStop: RndResizeCallback = useCallback(
        (e, dir, ref, delta, pos) => {
          // forward once on stop to ensure final size
          handleResize(e, dir, ref, delta, pos);
          setIsResizing(false); // unlock certain css
        },
        [handleResize],
      );

      // Attempt check and recover position if modal is offscreen for some reason
      // this shouldnt happen, but attempting at a safety net in case it does
      // note: modal min/max consts, if the window is smaller than those minimums
      // since this is taking those into account it wont try to recover if the modal
      // is cut off due to the window being too small to fit the modal min/max sizes...
      // to test: setup a useInterval and force state oob x/y every 5 sec
      const checkAndFixSizeAndPos = useCallback(() => {
        if (!open || !rndRef?.current) {
          return;
        }
        // In mobile we are overriding certain styles like transform/translate
        // via a parent element direct styles on rnd were causing issues
        // so even if we extract off the rndEl the transform css (which rnd inlines)
        // we wouldnt be able to easily detect the proper values for that...
        // and also we would need some strange regex if we did try to account for
        // what rnd applies as transform/translate (since they dont use abs position)
        // and hope there is no other scale or other css applied...
        // for now - its likely better to skip the bounds checker on mobile/fs
        // and rely on overriding via css so browser itself enforces the bounds...
        if (isMobileOrFull) {
          return;
        }

        // Extract the rnd element and its bounding rect
        const rndEl = rndRef?.current?.getSelfElement();
        if (!rndEl) {
          return;
        }

        // The DOMRect returned here seems to have some
        // strange float precision issues for example
        // { x: 0.000...288, ... } so rounding all final values
        // to ensure we dont get incorrect offscreen issues causing
        // erroneously set the state over and over again...
        // also helps deal with the subpixel blur issue as some
        // browsers were incorrectly rendering the modal with subpixel values
        // and because rnd uses transform was causing the modal to look blurry
        // https://developer.mozilla.org/en-US/docs/Web/API/DOMRect
        const rect = rndEl.getBoundingClientRect();
        const rectLeft = Math.round(rect.left);
        const rectTop = Math.round(rect.top);
        const rectRight = Math.round(rect.right);
        const rectBottom = Math.round(rect.bottom);
        const rectWidth = Math.round(rect.width);
        const rectHeight = Math.round(rect.height);

        // Max position values to keep modal inside
        // viewport/container within max percent
        const maxWidth = Math.round(
          Math.min(
            document.documentElement.clientWidth * stateMaxWPct,
            document.documentElement.clientWidth,
          ),
        );

        const maxHeight = Math.round(
          Math.min(
            document.documentElement.clientHeight * stateMaxHPct,
            document.documentElement.clientHeight,
          ),
        );

        // Ensure width and height but respect min/max size constraints
        const width = Math.round(Math.max(stateMinW, Math.min(rectWidth, maxWidth)));

        const height = Math.round(Math.max(stateMinH, Math.min(rectHeight, maxHeight)));

        // Ensure the modal position within viewport/container boundaries
        const maxX = Math.round(
          Math.max(0, document.documentElement.clientWidth - width),
        );

        const maxY = Math.round(
          Math.max(0, document.documentElement.clientHeight - height),
        );

        // Check if modal is offscreen or oversized
        const isOversized = rectWidth > maxWidth || rectHeight > maxHeight;
        const isOffscreen =
          rectLeft < 0 ||
          rectTop < 0 ||
          rectRight > document.documentElement.clientWidth ||
          rectBottom > document.documentElement.clientHeight;

        const isOutOfBounds = isOffscreen || isOversized;

        // not out of bounds no need to update state
        if (!isOutOfBounds) {
          return;
        }

        // else we need to setup the new state payload
        const newX = Math.round(Math.max(0, Math.min(rectLeft, maxX)));
        const newY = Math.round(Math.max(0, Math.min(rectTop, maxY)));
        const newState: SizeAndPos = { width, height, x: newX, y: newY };

        // setState if current state is not the same as new size/pos state
        const needsStateUpdate =
          newState.x !== modalX ||
          newState.y !== modalY ||
          newState.width !== modalW ||
          newState.height !== modalH;

        if (needsStateUpdate) {
          //  console.warn('Modal out of bounds, attempting recovery.');
          rndRef?.current?.updateSize({ width: newState.width, height: newState.height });
          rndRef?.current?.updatePosition({ x: newState.x, y: newState.y });
          setSizeAndPos(newState);
        }
      }, [
        open,
        rndRef,
        isMobileOrFull,
        modalX,
        modalY,
        modalW,
        modalH,
        stateMinW,
        stateMinH,
        stateMaxWPct,
        stateMaxHPct,
      ]);

      // Debounced check function to prevent excessive executions
      const dbCheckAndFixSizeAndPos = useDebouncedCallback(checkAndFixSizeAndPos, {
        delay: 500,
      });

      // Debounced version of size/pos state for useEffect to not go wild during drag/resizes
      const [dbSizeAndPosition] = useDebouncedValue(sizeAndPosition, 500, {
        leading: true,
      });

      // Check bounds and recover size/pos if open on state changes
      useEffect(() => {
        if (open) {
          dbCheckAndFixSizeAndPos();
        }
      }, [open, dbSizeAndPosition, dbCheckAndFixSizeAndPos]);

      // test boundary checker - force out of bounds
      // every 5 sec (ensure browser is small enough)
      // useInterval(() => {
      //    setSizeAndPos((prev) => ({ ...prev, x: 1000, y: 500 }));
      //  }, 5000, { autoInvoke: true });

      // alternate strategy - polling if modal is offscreen and try recover position
      // const recoveryInterval = useInterval(checkAndFixSizeAndPos, 1000, { autoInvoke: false });
      // start/stop recovery interval based on modal open state
      // useEffect(() => {
      //   if (open) {
      //     if (!recoveryInterval.active) {
      //       recoveryInterval.start(); // start polling when open
      //     }
      //   } else {
      //     recoveryInterval.stop(); // stop polling if closed
      //   }
      //   return () => recoveryInterval.stop(); // cleanup
      // }, [open, recoveryInterval]);

      // Very slightly debounced versions of drag and resize handlers
      // especially drag event if not debounced can emit thousands of events during drag
      // which causes a huge amount of state updates and rerenders, so tiny ms debounce
      // If resize/drag feels laggy then we will have to remove the debounces or lower delays
      const dbHandleDrag = useDebouncedCallback(handleDrag, { delay: 2 });
      const dbHandleResize = useDebouncedCallback(handleResize, { delay: 1 });

      // prevent mousedown drag on close btn
      const handleCloseMouseDown = useCallback(
        (event: React.MouseEvent<HTMLSpanElement>) => {
          event.stopPropagation();
        },
        [],
      );

      // dispatch to local onClose handler as closeButton
      const handleCloseBtnClick = useCallback(
        (event: React.MouseEvent<HTMLSpanElement>) => {
          internalOnClose(event, 'closeButton');
        },
        [internalOnClose],
      );

      // prevent mousedown drag on fullscr btn
      const handleFsMouseDown = useCallback(
        (event: React.MouseEvent<HTMLSpanElement>) => {
          event.stopPropagation();
        },
        [],
      );

      // toggle fullscreen state
      const handleFullScrBtnClick = useCallback(
        (event: React.MouseEvent<HTMLSpanElement>) => {
          setIsFullscreen((prev) => !prev);
        },
        [],
      );

      // If using the default id, must namespace it to make it unique...
      // with a cleaned reactId so it wont conflict with other DragModals
      // that may be above/underneath this one in the DOM tree...
      // as it must be able to pass `element.matches()` or querySelector so raw useId
      // by default may be invalidly parsed eg ":r2m:" and the colons arent valid there
      // thus we will need to have a sanitized version of the reactId prefix...
      // NOTE: However if passed in custom id we must trust its unique and valid.
      const isDefaultId = useMemo(() => Boolean(id === DEFAULT_ID), [id]);
      const cleanReactId = useMemo(() => sanitizeReactUseId(reactId), [reactId]);
      const baseId = isDefaultId ? `${DEFAULT_ID}-${cleanReactId}` : id;
      const modalId = baseId;
      const rndId = `${baseId}-rnd`;
      const paperId = `${baseId}-paper`;
      const loaderId = `${baseId}-loader`;
      const focusTrapId = `${baseId}-focustrap`;
      const headerDragHandleId = `${baseId}-header`; // header and drag area id
      const headerStartId = `${headerDragHandleId}-start`;
      const headerMidId = `${headerDragHandleId}-middle`;
      const headerEndId = `${headerDragHandleId}-end`;
      const bodyId = `${baseId}-body`;
      const footerId = `${baseId}-footer`;

      // Setup resize handle classnames for styling workarounds
      // note: had some trouble getting the rnd prop resizeHandleStyles
      // to apply hover styles since it seems to apply inline and
      // css doesnt support pseudo classes inline, so as a workaround
      // we can target the classnames directly from the parent element
      // but if you have more static styles for the handles you can
      // create resizeHandleStyles:HandleStyles and pass it to rnd
      // also it supports components per slot per the prop
      // resizeHandleComponent:HandleComponent if we need more control
      const resizeHandleClassNames: HandleClasses = useMemo(
        () => ({
          top: `${rndId}-handle-top`,
          right: `${rndId}-handle-right`,
          bottom: `${rndId}-handle-bottom`,
          left: `${rndId}-handle-left`,
          topLeft: `${rndId}-handle-topleft`,
          topRight: `${rndId}-handle-topright`,
          bottomLeft: `${rndId}-handle-bottomleft`,
          bottomRight: `${rndId}-handle-bottomright`,
        }),
        [rndId],
      );

      // Paper styles for the modal
      // Important: A user can technically 'release' the resize handle
      // below the min or above the max and the resize handles can get
      // stuck in those positions that they were released at
      // so ensure the the min/max of the modal state always respects
      // the min/max values set in the paper or modal styles prop

      // todo: test optimize style recalculations with will-change on paperSx
      // willChange needs some testing to see if it helps with performance
      // but it can also cause worse perf or incorrect stacking context (eg z-index)
      // so we need to test the performance before committing to using it
      // https://developer.mozilla.org/en-US/docs/Web/CSS/will-change
      // https://www.digitalocean.com/community/tutorials/css-will-change
      // willChange:'width, height, max-width, max-height, min-width, min-height, border-radius, box-shadow',
      // on the rnd element itself we should additionally set in conjunction with the paper styles
      // style={{ willChange: 'transform, width, height, left, top, display', }}
      const paperBoxSx: SxProps<Theme> = useMemo(
        () => ({
          width: isMobileOrFull ? '100svw' : modalW,
          height: isMobileOrFull ? '100svh' : modalH,
          maxWidth: isMobileOrFull ? '100svw' : stateMaxW,
          maxHeight: isMobileOrFull ? '100svh' : stateMaxH,
          minWidth: isMobileOrFull ? '100svw' : stateMinW,
          minHeight: isMobileOrFull ? '100svh' : stateMinH,
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          gridTemplateAreas: `"modalheader" "modalbody" "modalfooter"`,
          container: 'modalpaper / inline-size',
          overflow: 'hidden',
          position: 'relative',
          borderRadius: isMobileOrFull ? 0 : 1,
          bgcolor: theme.palette.background.paper,
          boxShadow: isMobileOrFull ? 'none' : theme.shadows[3],
          // no pointer events if dragging or resizing
          // for edge case if user releases mouse over paper
          // help prevent stuck in dragging or resizing mode
          pointerEvents: midDragOrResize ? 'none' : 'auto',
          // no user select if dragging or resizing
          userSelect: midDragOrResize ? 'none' : 'auto',
          ...(typeof paperSx === 'function' ? paperSx(theme) : paperSx),
        }),
        [
          isMobileOrFull,
          midDragOrResize,
          modalH,
          modalW,
          paperSx,
          stateMaxH,
          stateMaxW,
          stateMinH,
          stateMinW,
          theme,
        ],
      );

      // Workaround to target certain styles via parent wrapper to the rnd
      // and mui modal child element must be able to hold a ref for focustrap.
      // the actual style prop seems to be misleading on rnd as even static
      // style attributes didnt seem to apply properly when on rnd style prop.
      // notably transform and a few others which must be removed on mobile
      // as well as need to target the resize handle classes somehow...
      // when transitioning from desktop to mobile very rarely the transform
      // of the rnd would stay applied causing a misalignment of our fullscreen mode
      // so we will need to target everything from this parent for overrides...
      const focusTrapWrapSx: SxProps<Theme> = useMemo(
        () => ({
          // Allow pointer events to pass through when allowDragThrough is enabled
          pointerEvents: allowDragThrough ? 'none' : undefined,

          // specific styles workaround targetting rnd itself
          // mostly for mobile to match or be compatible with our paper styles
          // the !important is required to override the rnd inline styles
          [`& #${rndId}`]: {
            // Re-enable pointer events on the modal content itself
            pointerEvents: allowDragThrough ? 'auto' : undefined,
            // basically remove rnd element in mobile/full
            display: isMobileOrFull ? 'contents !important' : undefined,
            // also remove any rnd transform properties
            transform: isMobileOrFull ? 'none !important' : undefined,
          },

          // hide or show the resize handles
          // via desired resizeHandles config
          [`& .${resizeHandleClassNames.top}`]: {
            display: resizeHandles.top ? 'block' : 'none',
          },
          [`& .${resizeHandleClassNames.right}`]: {
            display: resizeHandles.right ? 'block' : 'none',
          },
          [`& .${resizeHandleClassNames.bottom}`]: {
            display: resizeHandles.bottom ? 'block' : 'none',
          },
          [`& .${resizeHandleClassNames.left}`]: {
            display: resizeHandles.left ? 'block' : 'none',
          },
          [`& .${resizeHandleClassNames.topLeft}`]: {
            display: resizeHandles.topLeft ? 'block' : 'none',
          },
          [`& .${resizeHandleClassNames.topRight}`]: {
            display: resizeHandles.topRight ? 'block' : 'none',
          },
          [`& .${resizeHandleClassNames.bottomLeft}`]: {
            display: resizeHandles.bottomLeft ? 'block' : 'none',
          },
          [`& .${resizeHandleClassNames.bottomRight}`]: {
            display: resizeHandles.bottomRight ? 'block' : 'none',
          },

          // Resize handle hover styles targetting workaround
          [`& .${resizeHandleClassNames.top}:hover,
            & .${resizeHandleClassNames.right}:hover,
            & .${resizeHandleClassNames.bottom}:hover,
            & .${resizeHandleClassNames.left}:hover,
            & .${resizeHandleClassNames.topLeft}:hover,
            & .${resizeHandleClassNames.topRight}:hover,
            & .${resizeHandleClassNames.bottomLeft}:hover,
            & .${resizeHandleClassNames.bottomRight}:hover`]: {
            backgroundColor: theme.palette.action.disabled,
            borderRadius: 4,
          },
        }),
        [
          allowDragThrough,
          isMobileOrFull,
          resizeHandleClassNames.bottom,
          resizeHandleClassNames.bottomLeft,
          resizeHandleClassNames.bottomRight,
          resizeHandleClassNames.left,
          resizeHandleClassNames.right,
          resizeHandleClassNames.top,
          resizeHandleClassNames.topLeft,
          resizeHandleClassNames.topRight,
          resizeHandles.bottom,
          resizeHandles.bottomLeft,
          resizeHandles.bottomRight,
          resizeHandles.left,
          resizeHandles.right,
          resizeHandles.top,
          resizeHandles.topLeft,
          resizeHandles.topRight,
          rndId,
          theme.palette.action.disabled,
        ],
      );

      // Optimizing style recalculations
      const headerBoxSx: SxProps<Theme> = useMemo(
        () => ({
          gridArea: 'modalheader',
          container: 'modalheader / inline-size',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          justifyContent: 'space-between',
          padding: PAD_VAL,
          borderBottom: `1px solid ${theme.palette.divider}`,
          cursor: isDragAllowed ? 'move' : undefined,
          bgcolor: isDragging ? getSolidThemeGray(theme, true) : getSolidThemeGray(theme),
          // mid drag or resize overrides
          overflow: midDragOrResize ? 'hidden' : undefined,
          userSelect: midDragOrResize ? 'none' : undefined,
          color: theme.palette.text.primary,
        }),
        [isDragAllowed, isDragging, midDragOrResize, theme],
      );

      /**
       * CMSADMIN-1929 - cancel css string for buttons
       * in the header that should not trigger drag events
       * instead allow event to bubble up to the button,
       * alternate fix is to put header into two sections eg
       * in a grid [draghandle & jsx header] [action buttons]
       * and attach the left with the draghandle ref/class
       */
      const dragCancelClasses = useMemo(
        () => `.${headerEndId}, #${modalId}-close-button, #${modalId}-fullscreen-button`,
        [headerEndId, modalId],
      );

      // Linear progress bar styles
      const loaderSx: SxProps<Theme> = useMemo(
        () => ({
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          height: '4px',
          bgcolor: isDragging ? getSolidThemeGray(theme, true) : getSolidThemeGray(theme),
          opacity: showLoadingBar ? 1 : 0,
          display: showLoadingBar ? 'block' : 'none',
          transition: theme.transitions.create(['opacity'], {
            duration: transitionDurationMs,
            delay: transitionDurationMs,
          }),
          '& .MuiLinearProgress-bar': {
            animationDuration: '4s',
            transition: 'none',
          },
        }),
        [isDragging, showLoadingBar, theme, transitionDurationMs],
      );

      // Masking pseduo element styles
      const maskElSx: SxProps<Theme> = useMemo(
        () => ({
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundColor: getSolidThemeGray(theme, true),
            opacity: 0.75,
            zIndex: 2,
            transition: theme.transitions.create('opacity', {
              duration: transitionDurationMs,
            }),
          },
        }),
        [theme, transitionDurationMs],
      );

      // Body wrapper styles
      const bodyBoxSx: SxProps<Theme> = useMemo(
        () => ({
          gridArea: 'modalbody',
          container: 'modalbody / inline-size',
          position: 'relative',
          padding: PAD_VAL,
          display: 'flex',
          flexDirection: 'column',
          color: theme.palette.text.primary,
          // stable scrollbars as it hides itself if it can scroll mid drag, shifting inner contents
          scrollbarGutter: 'stable both-edges',
          // mid drag or resize overrides
          overflow: midDragOrResize ? 'hidden' : 'auto',
          userSelect: midDragOrResize ? 'none' : undefined,
          ...(shouldMask && { ...maskElSx }),
          // props passed body sx
          ...(typeof bodySx === 'function' ? bodySx(theme) : bodySx),
        }),
        [bodySx, maskElSx, midDragOrResize, shouldMask, theme],
      );

      // Footer wrapper styles
      const footerBoxSx: SxProps<Theme> = useMemo(
        () => ({
          gridArea: 'modalfooter',
          container: 'modalfooter / inline-size',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          color: theme.palette.text.primary,
          bgcolor: getSolidThemeGray(theme),
          padding: hasFooter ? PAD_VAL : 0,
          borderTop: hasFooter ? `1px solid ${theme.palette.divider}` : undefined,
          // mid drag or resize overrides
          overflow: midDragOrResize ? 'hidden' : undefined,
          userSelect: midDragOrResize ? 'none' : undefined,
          ...(shouldMask && { ...maskElSx }),
        }),
        [hasFooter, maskElSx, midDragOrResize, shouldMask, theme],
      );

      // Parent modal slot props
      // Conditionally allow dragdrop from modal to elements outside
      const modalSlotProps: ModalProps['slotProps'] = useMemo(() => {
        const slotProps: ModalProps['slotProps'] = {
          backdrop: {
            sx: allowDragThrough
              ? { pointerEvents: 'none', backgroundColor: 'transparent' }
              : undefined,
          },
        };
        return slotProps;
      }, [allowDragThrough]);

      return (
        <Modal
          ref={ref}
          id={modalId}
          data-testid={modalId}
          data-fullscreen={isFullscreen}
          className={modalId}
          open={open}
          onClose={internalOnClose}
          hideBackdrop={hideBackdrop}
          slotProps={modalSlotProps}
          {...props}
        >
          <Box
            ref={focusTrapRef}
            id={focusTrapId}
            data-testid={focusTrapId}
            sx={focusTrapWrapSx}
          >
            <Rnd
              // note the style prop on rnd behaves strangely and
              // certain values dont work on it - see comment focusTrapWrapSx
              ref={rndRef}
              id={rndId}
              data-testid={rndId}
              // attempt to workaround subpixel precision blur issues
              // by keeping this always on a 1x1 pixel grid and also
              // rounding all size/pos values to avoid float precision
              dragGrid={[1, 1]}
              resizeGrid={[1, 1]}
              scale={1}
              size={{ width: modalW, height: modalH }}
              position={{ x: modalX, y: modalY }}
              // pass minmax states to rnd via props so that internally
              // rnd can calculate the resize handle positions correctly
              // fixes edge case issue where resize handles could be
              // shown in wrong position after extreme browser resizes.
              // eg if browser was resized below the the minWidth prop then
              // resized back to a larger size without interacting with modal
              maxHeight={stateMaxH}
              maxWidth={stateMaxW}
              minHeight={stateMinH}
              minWidth={stateMinW}
              // uncertain if default prop is needed as the values
              // are derived from our state prop anyway and seemed
              // to work with or without default prop set
              default={{ x: modalX, y: modalY, width: modalW, height: modalH }}
              // todo: allow for custom container prop to be passed in
              // via mui modal container prop and reslove and use it as bounds
              // see comment on helper fn resolveModalContainer
              bounds="window"
              onDrag={dbHandleDrag}
              onDragStop={handleDragStop}
              onDragStart={handleDragStart}
              onResize={dbHandleResize}
              onResizeStart={handleResizeStart}
              onResizeStop={handleResizeStop}
              enableResizing={isResizeAllowed}
              disableDragging={!isDragAllowed}
              dragHandleClassName={headerDragHandleId}
              resizeHandleClasses={resizeHandleClassNames}
              cancel={dragCancelClasses}
            >
              <Box
                id={paperId}
                data-testid={paperId}
                className={paperId}
                sx={paperBoxSx}
              >
                {shouldConfirmClose && (
                  <ConfirmCloseJSX
                    open={showConfirmClose}
                    onCancel={handleConfirmCancelClose}
                    onConfirm={handleConfirmCancelClose}
                    {...defaultConfirmCloseProps}
                  />
                )}

                <Box
                  // entire header is draghandle
                  ref={headerRef}
                  id={headerDragHandleId}
                  data-testid={headerDragHandleId}
                  className={headerDragHandleId}
                  sx={headerBoxSx}
                >
                  <LinearProgress
                    id={loaderId}
                    data-testid={loaderId}
                    sx={loaderSx}
                    {...linearProgressProps}
                  />
                  <Box
                    id={headerStartId}
                    data-testid={headerStartId}
                    className={headerStartId}
                    sx={{ display: 'flex', alignItems: 'center' }}
                  >
                    {isDragAllowed && (
                      <DragIndicatorIcon
                        sx={{
                          fontSize: bloxValues.iconSizeRegular,
                          color: theme.palette.text.secondary,
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    id={headerMidId}
                    data-testid={headerMidId}
                    className={headerMidId}
                    sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}
                  >
                    {header}
                  </Box>

                  <Box
                    id={headerEndId}
                    data-testid={headerEndId}
                    className={headerEndId}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {showFullscreenButton && (
                      <Tooltip
                        disableInteractive={true}
                        title={isFullscreen ? strRestore : strMaximize}
                      >
                        <IconButton
                          id={`${modalId}-fullscreen-button`}
                          data-testid={`${modalId}-fullscreen-button`}
                          component="span"
                          size="small"
                          onMouseDown={handleFsMouseDown}
                          onClick={handleFullScrBtnClick}
                          sx={{ fontSize: bloxValues.iconSizeRegular }}
                        >
                          {isFullscreen ? (
                            <FullscreenExitRoundedIcon
                              sx={{
                                fontSize: 'inherit',
                                color: theme.palette.text.secondary,
                              }}
                            />
                          ) : (
                            <FullscreenRoundedIcon
                              sx={{
                                fontSize: 'inherit',
                                color: theme.palette.text.secondary,
                              }}
                            />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip
                      title={strClose}
                      disableInteractive={true}
                    >
                      <IconButton
                        aria-label="close"
                        id={`${modalId}-close-button`}
                        data-testid={`${modalId}-close-button`}
                        component="span"
                        size="small"
                        onMouseDown={handleCloseMouseDown}
                        onClick={handleCloseBtnClick}
                        sx={{ fontSize: bloxValues.iconSizeRegular }}
                      >
                        <CloseRoundedIcon
                          sx={{
                            fontSize: 'inherit',
                            color: theme.palette.text.secondary,
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box
                  ref={bodyRef}
                  id={bodyId}
                  data-testid={bodyId}
                  className={bodyId}
                  sx={bodyBoxSx}
                  {...inertAttrs}
                >
                  {body}
                </Box>
                {footer && (
                  <Box
                    ref={footerRef}
                    id={footerId}
                    data-testid={footerId}
                    className={footerId}
                    sx={footerBoxSx}
                    {...inertAttrs}
                  >
                    {footer}
                  </Box>
                )}
              </Box>
            </Rnd>
          </Box>
        </Modal>
      );
    },
  ),
);
DragModal.displayName = 'DragModal';

/**
 * Drag Modal Default Confirm Close Component (Generic)
 * Can pass a custom component to the DragModal props to override this
 * or override the strings via DragModal props if needed
 */

// partial type to pass via dragmodal default props when
// using default cc overlay (dm handles open/confirm/cancel fns)
export type DefaultConfirmCloseProps = {
  title?: React.ReactNode;
  content?: React.ReactNode;
  subtext?: React.ReactNode;
  cancelTxt?: React.ReactNode;
  confirmTxt?: React.ReactNode;
  paperSx?: SxProps<Theme>;
  overlaySx?: SxProps<Theme>;
  /**
   * Use default warning strings when not provided?
   * default: true
   */
  withFallbacks?: boolean;
};

// By default uses translated preset warnings
const defaultCloseProps: DefaultConfirmCloseProps = {
  withFallbacks: true,
};

// base props for similar cc modals
export type BaseConfirmCloseProps = {
  open: boolean;
  onConfirm: DragModalProps['onClose'];
  onCancel: DragModalProps['onClose'];
};

// combined props for default confirm close component
type DefaultCloseJSXProps = DefaultConfirmCloseProps & BaseConfirmCloseProps;

export const DefaultConfirmCloseOverlay = memo(
  forwardRef<HTMLDivElement, DefaultCloseJSXProps>(
    (
      {
        open,
        onCancel,
        onConfirm,
        title,
        content,
        subtext,
        cancelTxt,
        confirmTxt,
        paperSx,
        overlaySx,
        withFallbacks = true,
      },
      ref,
    ) => {
      const theme = useTheme();
      const { t } = useTranslation();
      const focusTrapRef = useFocusTrap(open);
      const mergedRef = useMergedRef(ref, focusTrapRef);

      // moved strings into component so translate fn is reactive
      const resolvedTitle = useMemo(() => {
        if (title !== undefined) {
          return title;
        }
        if (withFallbacks) {
          return t('blox.modal.confirmClose');
        }
        return null;
      }, [title, withFallbacks, t]);

      const resolvedContent = useMemo(() => {
        if (content !== undefined) {
          return content;
        }
        if (withFallbacks) {
          return t('blox.modal.confirmClosePrimary');
        }
        return null;
      }, [content, withFallbacks, t]);

      const resolvedSubtxt = useMemo(() => {
        if (subtext !== undefined) {
          return subtext;
        }
        if (withFallbacks) {
          return t('blox.modal.confirmCloseSecondary');
        }
        return null;
      }, [subtext, withFallbacks, t]);

      const resolvedCancel = useMemo(() => {
        if (cancelTxt !== undefined) {
          return cancelTxt;
        }
        if (withFallbacks) {
          return t('blox.modal.cancelCloseBtn');
        }
        return null;
      }, [cancelTxt, withFallbacks, t]);

      const resolvedConfirm = useMemo(() => {
        if (confirmTxt !== undefined) {
          return confirmTxt;
        }
        if (withFallbacks) {
          return t('blox.modal.confirmCloseBtn');
        }
        return null;
      }, [confirmTxt, withFallbacks, t]);

      const dialogOverlaySx: SxProps<Theme> = useMemo(() => {
        const resolvedSx = typeof overlaySx === 'function' ? overlaySx(theme) : overlaySx;
        return {
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: open ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#00000033',
          zIndex: theme.zIndex.modal + 2,
          ...resolvedSx,
        };
      }, [theme, overlaySx, open]);

      const dialogPaperSx: SxProps<Theme> = useMemo(() => {
        const resolvedSx = typeof paperSx === 'function' ? paperSx(theme) : paperSx;
        return {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 2,
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.shadows[8],
          overflow: 'hidden',
          background: theme.palette.background.paper,
          zIndex: theme.zIndex.modal + 4,
          maxWidth: '425px',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          ...resolvedSx,
        };
      }, [theme, paperSx]);

      // Prevent click propagation to modal
      const boxOnClick = useCallback((e: React.MouseEvent) => {
        if (e?.stopPropagation) {
          e.stopPropagation();
        }
      }, []);

      // user wishes to close the modal
      const confirmCloseOnClick = useCallback(
        (e: React.MouseEvent) => {
          if (onConfirm) {
            onConfirm(e, 'confirmClose');
          }
        },
        [onConfirm],
      );

      // user wishes to keep the modal open
      const cancelCloseOnClick = useCallback(
        (e: React.MouseEvent) => {
          if (onCancel) {
            onCancel(e, 'cancelClose');
          }
        },
        [onCancel],
      );

      // dispatch to local cancel handler on header x
      const handleCloseBtnClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
          cancelCloseOnClick(event);
        },
        [cancelCloseOnClick],
      );

      return (
        <Box
          // focustrap + forward ref to parent
          ref={mergedRef}
          id="confirm-close-overlay"
          data-testid="confirm-close-overlay"
          sx={dialogOverlaySx}
          onClick={boxOnClick}
        >
          <Paper
            id="confirm-close-paper"
            data-testid="confirm-close-paper"
            sx={dialogPaperSx}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: 'row',
                borderBottom: `1px solid ${theme.palette.divider}`,
                gap: 1,
                padding: 1.5,
                bgcolor: getSolidThemeGray(theme),
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  gap: 1,
                }}
              >
                <WarningIcon
                  color="error"
                  sx={{ fontSize: bloxValues.iconSizeRegular }}
                />
                <Typography
                  sx={{
                    fontSize: bloxValues.xxlFontSizeRem,
                    fontWeight: bloxValues.fontWeightMedium,
                    color: theme.palette.error.main,
                  }}
                >
                  {resolvedTitle}
                </Typography>
              </Box>

              <Tooltip
                title={resolvedCancel}
                disableInteractive={true}
              >
                <IconButton
                  data-testid="cancel-close-header-button"
                  size="small"
                  onClick={handleCloseBtnClick}
                  sx={{ fontSize: bloxValues.iconSizeRegular }}
                >
                  <CloseRoundedIcon
                    sx={{
                      fontSize: 'inherit',
                      color: theme.palette.text.secondary,
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                padding: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: bloxValues.xlFontSizeRem,
                  fontWeight: bloxValues.fontWeightMedium,
                  color: theme.palette.text.primary,
                }}
              >
                {resolvedContent}
              </Typography>
              <Typography
                sx={{
                  fontSize: bloxValues.regularFontSizeRem,
                  fontWeight: bloxValues.fontWeightRegular,
                  color: theme.palette.text.secondary,
                }}
              >
                {resolvedSubtxt}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                borderTop: `1px solid ${theme.palette.divider}`,
                gap: 2,
                padding: 1.5,
                bgcolor: getSolidThemeGray(theme),
              }}
            >
              <Button
                color="error"
                variant="contained"
                id="confirm-close-button"
                data-testid="confirm-close-button"
                onClick={confirmCloseOnClick}
                disableElevation={true}
                sx={{
                  fontSize: bloxValues.regularFontSizeRem,
                }}
              >
                {resolvedConfirm}
              </Button>
              <Button
                color="primary"
                variant="contained"
                id="cancel-close-button"
                data-testid="cancel-close-button"
                onClick={cancelCloseOnClick}
                disableElevation={true}
                sx={{
                  fontSize: bloxValues.regularFontSizeRem,
                }}
              >
                {resolvedCancel}
              </Button>
            </Box>
          </Paper>
        </Box>
      );
    },
  ),
);
DefaultConfirmCloseOverlay.displayName = 'DefaultConfirmCloseOverlay';

/**
 * DragModalHeader Component
 * Header component for DragModal that displays an optional icon and header text
 * with consistent styling matching the existing implementation
 */
export interface DragModalHeaderProps {
  /**
   * Optional icon to display before the header text
   */
  icon?: React.ReactNode;

  /**
   * The header text to display
   */
  text: string;
}

export const DragModalHeader = memo(
  forwardRef<HTMLDivElement, DragModalHeaderProps>(({ icon, text }, ref) => {
    const theme = useTheme();

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {icon && icon}
        <Typography
          sx={{
            fontSize: bloxValues.xlFontSizeRem,
            fontWeight: bloxValues.fontWeightMedium,
            color: theme.palette.text.primary,
          }}
        >
          {text}
        </Typography>
      </Box>
    );
  }),
);

DragModalHeader.displayName = 'DragModalHeader';
